import { useEffect, useRef, useState } from 'react';

import { CHAT_SCRIPT } from './chat-script';

import type { Exchange } from './chat-script';
import type { RefObject } from 'react';

/**
 * Drives the how-it-works stage's live conversation.
 *
 * A visitor arrives mid-conversation (the thread is SEEDED with the last two
 * exchanges) and then watches the next one happen: the question types itself
 * into the composer, sends, the agent thinks, and the answer lands with its
 * card. Seeding matters — an empty chat box that slowly fills reads as a
 * loading state, and the first impression of the product should be a
 * conversation already in progress.
 *
 * ## Typing touches the DOM directly
 *
 * A `setState` per character would re-render the whole band ~30 times a second
 * for the entire page's lifetime. The composer's text node is written to
 * directly instead; it renders no React children, so nothing can clobber it.
 *
 * ## It stops when nobody is watching
 *
 * The loop pauses when the stage scrolls out of view or the tab is hidden, and
 * never starts at all under `prefers-reduced-motion` — where it seeds the
 * thread and stops, leaving a complete, readable conversation rather than an
 * empty box. An animation nobody is looking at is pure battery cost.
 */

export interface ChatTurn {
  readonly key: string;
  readonly side: 'user' | 'agent';
  readonly textKey: string;
  /** Present on the agent turn that carries the exchange's result card. */
  readonly exchange?: Exchange;
}

interface ChatSim {
  readonly viewRef: RefObject<HTMLDivElement>;
  readonly composerRef: RefObject<HTMLSpanElement>;
  readonly turns: readonly ChatTurn[];
  /** The agent is working; show the thinking indicator with this label. */
  readonly workKey: string | null;
}

const TYPE_MS = 32;
const SEND_PAUSE_MS = 420;
const THINK_MS = 1450;
const READ_MS = 3400;

function seed(): ChatTurn[] {
  // The two exchanges BEFORE the first one that will play, so the visitor
  // never sees the same exchange twice in a row on the first loop.
  return CHAT_SCRIPT.slice(-2).flatMap((exchange) => [
    { key: `${exchange.id}-u`, side: 'user' as const, textKey: exchange.askKey },
    { key: `${exchange.id}-a`, side: 'agent' as const, textKey: exchange.replyKey, exchange },
  ]);
}

export function useChatSim(
  active: boolean,
  placeholder: string,
  translate: (k: string) => string,
): ChatSim {
  const viewRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLSpanElement>(null);
  const [turns, setTurns] = useState<ChatTurn[]>(seed);
  const [workKey, setWorkKey] = useState<string | null>(null);

  // `translate` changes identity on every render; the loop needs the CURRENT
  // one without restarting, so it reads through a ref rather than a dependency.
  const translateRef = useRef(translate);
  translateRef.current = translate;

  useEffect(() => {
    if (!active) return undefined;
    const reduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return undefined;

    let index = 0;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let typer: ReturnType<typeof setInterval> | undefined;

    const wait = (ms: number, then: () => void): void => {
      timer = setTimeout(() => {
        if (!cancelled) then();
      }, ms);
    };

    const type = (text: string, done: () => void): void => {
      const el = composerRef.current;
      if (!el) {
        // The ref can attach a frame late; retry rather than losing the turn.
        wait(200, () => {
          type(text, done);
        });
        return;
      }
      el.style.color = '#2f2f38';
      el.textContent = '';
      let i = 0;
      typer = setInterval(() => {
        if (cancelled) return;
        i += 2;
        el.textContent = text.slice(0, i);
        if (i >= text.length) {
          clearInterval(typer);
          done();
        }
      }, TYPE_MS);
    };

    const run = (): void => {
      if (cancelled) return;
      if (index >= CHAT_SCRIPT.length) {
        index = 0;
        // Keep the last exchange on screen so the reset is not a blank frame.
        setTurns((prev) => prev.slice(-2));
      }
      const exchange = CHAT_SCRIPT[index];
      // The script is a non-empty literal and `index` was just wrapped, so this
      // cannot miss — but the guard is cheaper than a crashed render loop if
      // the script is ever emptied.
      if (!exchange) return;
      type(translateRef.current(exchange.askKey), () => {
        wait(SEND_PAUSE_MS, () => {
          const el = composerRef.current;
          if (el) {
            el.textContent = placeholder;
            el.style.color = '#6e6e77';
          }
          setTurns((prev) => [
            ...prev,
            { key: `${exchange.id}-u-${index}`, side: 'user', textKey: exchange.askKey },
          ]);
          setWorkKey(exchange.workKey);
          wait(THINK_MS, () => {
            setTurns((prev) => [
              ...prev,
              {
                key: `${exchange.id}-a-${index}`,
                side: 'agent',
                textKey: exchange.replyKey,
                exchange,
              },
            ]);
            setWorkKey(null);
            index += 1;
            wait(READ_MS, run);
          });
        });
      });
    };

    wait(1400, run);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      if (typer) clearInterval(typer);
    };
  }, [active, placeholder]);

  // Keep the newest turn in view. `scrollTo` with `behavior` is not implemented
  // in jsdom and throws there, so the assignment fallback is load-bearing for
  // the component tests rather than for any browser.
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    try {
      view.scrollTo({ top: view.scrollHeight, behavior: 'smooth' });
    } catch {
      view.scrollTop = view.scrollHeight;
    }
  }, [turns]);

  return { viewRef, composerRef, turns, workKey };
}
